// DocumentCheckinForm.js - Main component
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import documentCheckinService from "../../lib/DocumentCheckinService";
import { useAuth } from "../../hooks/useAuth";
import FormHeader from "./components/FormHeader";
import FieldRenderer from "./components/FieldRenderer";
import FileUploadSection from "./components/FileUploadSection";
import SubmitButton from "./components/SubmitButton";
import {
  saveFormData,
  loadFormData,
  saveUploadedFiles,
  loadUploadedFiles,
  clearAllDataForConfig,
} from "./utils/storageUtils";

const DocumentCheckinForm = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (config && Object.keys(formData).length > 0) {
      saveFormData(config.id, formData);
    }
  }, [formData, config]);

  // Auto-save file data
  useEffect(() => {
    if (config && Object.keys(files).length > 0) {
      saveUploadedFiles(config.id, files);
    }
  }, [files, config]);

  // Load configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        // Load configuration from server
        const configData = await documentCheckinService.getConfigBySlug(slug);

        const parsedConfig = {
          ...configData,
          configuration: JSON.parse(configData.configurationJson),
        };
        setConfig(parsedConfig);

        // NOW load cached form data using the config ID we just got
        const cachedFormData = loadFormData(parsedConfig.id);
        const cachedFiles = loadUploadedFiles(parsedConfig.id);

        // Initialize form data: use cached values if they exist, otherwise empty string
        const initialFormData = {};
        parsedConfig.configuration.fields.forEach((field) => {
          initialFormData[field.id] = cachedFormData[field.id] || "";
        });
        setFormData(initialFormData);
        setFiles(cachedFiles);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadConfig();
    }
  }, [slug]);

  // Handle form field changes
  const handleFieldChange = (fieldId, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[fieldId]) {
      setValidationErrors((prev) => ({
        ...prev,
        [fieldId]: null,
      }));
    }
  };

  // Handle file changes
  const handleFileChange = (fileData) => {
    setFiles(fileData);

    // Clear validation error
    if (validationErrors.files) {
      setValidationErrors((prev) => ({
        ...prev,
        files: null,
      }));
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = {};

    config.configuration.fields.forEach((field) => {
      if (field.required) {
        if (field.type === "document_file") {
          if (!files[field.id] || files[field.id].length === 0) {
            errors[field.id] = `${field.title} is required`;
          }
        } else {
          if (!formData[field.id] || formData[field.id].trim() === "") {
            errors[field.id] = `${field.title} is required`;
          }
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      // Prepare user inputs (exclude file fields)
      const userInputs = config.configuration.fields
        .filter((field) => field.type !== "document_file")
        .map((field) => ({
          fieldName: field.id,
          fieldValue: formData[field.id],
        }));

      // Collect all files
      const allFiles = [];
      Object.values(files).forEach((fileArray) => {
        if (fileArray && fileArray.length > 0) {
          allFiles.push(...fileArray);
        }
      });

      // Submit checkin
      const result = await documentCheckinService.processDocumentCheckin(
        config.id,
        user?.uid || "anonymous",
        userInputs,
        allFiles,
        token
      );

      if (result.success) {
        // Clear cached data on successful submission
        clearAllDataForConfig(config.id);

        navigate(`/checkin/${slug}/success`, {
          state: { checkinId: result.result.id },
        });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800">
            Configuration not found
          </h2>
          <p className="text-gray-600 mt-2">
            The requested document type does not exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <FormHeader config={config} />

          <form onSubmit={handleSubmit}>
            {config.configuration.fields.map((field) => (
              <FieldRenderer
                key={field.id}
                field={field}
                value={formData[field.id] || ""}
                error={validationErrors[field.id]}
                onChange={(value) => handleFieldChange(field.id, value)}
                files={files}
                onFileChange={handleFileChange}
              />
            ))}

            <FileUploadSection
              config={config}
              files={files}
              onFileChange={handleFileChange}
              error={validationErrors.files}
            />

            <SubmitButton submitting={submitting} onSubmit={handleSubmit} />
          </form>
        </div>
      </div>
    </div>
  );
};

export default DocumentCheckinForm;
